/**
 * <s-product-carousel> — one-up product photo carousel.
 *
 * The track is a native scroll-snap row, so swiping and trackpad scrolling work without JS.
 * This component only wires the previous/next arrows and thumbnails, and keeps the
 * active thumbnail and arrow states in sync with the visible slide.
 *
 * Required markup:
 *   [data-carousel-track]          — horizontal scroll-snap container.
 *   [data-carousel-slide]          — each slide, a direct child of the track.
 *   [data-carousel-prev], [data-carousel-next] — optional arrow buttons.
 *   [data-carousel-thumb][data-index] — optional thumbnail buttons.
 */
class SProductCarousel extends HTMLElement {
  #ac;
  #track;
  #slides = [];
  #index = 0;
  #frame = 0;

  connectedCallback() {
    this.#track = this.querySelector('[data-carousel-track]');
    if (!this.#track) return;
    this.#slides = Array.from(this.#track.querySelectorAll(':scope > [data-carousel-slide]'));
    if (this.#slides.length < 2) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.querySelector('[data-carousel-prev]')?.addEventListener('click', () => this.goTo(this.#index - 1), opts);
    this.querySelector('[data-carousel-next]')?.addEventListener('click', () => this.goTo(this.#index + 1), opts);
    this.addEventListener('click', this.#handleThumb, opts);
    this.#track.addEventListener('scroll', this.#handleScroll, { ...opts, passive: true });
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  goTo(index) {
    const next = Math.max(0, Math.min(index, this.#slides.length - 1));
    const slide = this.#slides[next];
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollTo({ left: slide.offsetLeft - this.#track.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.#index = next;
    this.#update();
  }

  #handleThumb = (event) => {
    if (!(event.target instanceof Element)) return;
    const thumb = event.target.closest('[data-carousel-thumb]');
    if (!thumb || !this.contains(thumb)) return;
    this.goTo(Number(thumb.dataset.index));
  };

  #handleScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const width = this.#track.clientWidth || 1;
      const index = Math.round(Math.abs(this.#track.scrollLeft) / width);
      if (index !== this.#index) {
        this.#index = Math.max(0, Math.min(index, this.#slides.length - 1));
        this.#update();
      }
    });
  };

  #update() {
    const prev = this.querySelector('[data-carousel-prev]');
    const next = this.querySelector('[data-carousel-next]');
    if (prev) prev.disabled = this.#index === 0;
    if (next) next.disabled = this.#index === this.#slides.length - 1;

    for (const thumb of this.querySelectorAll('[data-carousel-thumb]')) {
      const active = Number(thumb.dataset.index) === this.#index;
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
    }

    const status = this.querySelector('[data-carousel-status]');
    if (status) status.textContent = `${this.#index + 1} / ${this.#slides.length}`;
  }
}

if (!customElements.get('s-product-carousel')) {
  customElements.define('s-product-carousel', SProductCarousel);
}
